import * as React from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { useLocation } from "react-router-dom";
import DiscountSearch, { groupBy, groupMapping } from "./DiscountSearch";
import AccordionList from "./AccordionList";
import { useDispatch, useSelector } from "react-redux";
import { ENTITY_TYPES, OVERRIDE_TYPE } from "../../utils";
import {
  setGlobalLoader,
  showNotifier,
  setSettings,
} from "../../store/globalSlice";
import useRequest from "../../hooks/useRequest";
import configData from "../../config";

const DiscountAndPlanogram = () => {
  const { requestMaker } = useRequest();
  const [aD, setAD] = React.useState(groupBy[0]);
  const [displaySelectedOnly, setDisplaySelectedOnly] = React.useState(false);
  const [filterData, setFilterData] = React.useState([]);
  const [filterValues, setFilterValues] = React.useState([]);
  const [tableData, setTableData] = React.useState({});
  const [itemData, setItemData] = React.useState({});
  const store = useSelector((store) => store.global.store);
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const currentEntity = React.useMemo(() => {
    let currentEntityType = "";
    switch (pathname) {
      case "/discounting":
        currentEntityType = ENTITY_TYPES.discount;
        break;
      case "/manage-planograms":
        currentEntityType = ENTITY_TYPES.planogram;
        break;
      case "/manage-orders":
        currentEntityType = ENTITY_TYPES.orders;
        break;
      default:
        currentEntityType = ENTITY_TYPES.discount;
    }
    dispatch(setSettings({ entityType: currentEntityType }));
  }, [pathname, dispatch]);

  const handleItemDetails = React.useCallback(
    (
      entityScope,
      isItemDisplay,
      revertSelectionOnError,
      setDetailsOnSuccess
    ) => {
      const getScopeMapping = () => {
        const mappings = {
          Department: ["departmentName"],
          Category: ["departmentName", "className"],
          Planogram: ["departmentName", "className", "subClassName"],
          "Planogram #": [
            "departmentName",
            "className",
            "subClassName",
            "planogramName",
          ],
          SKU: ["SKU"],
          Vendor: ["departmentName", "className", "subClassName", "vendor"],
        };
        const updatedFilters = mappings[aD].reduce(
          (acc, item) => {
            let types = {};
            switch (item) {
              case "departmentName":
                types = { departmentName: entityScope };
                break;
              case "className":
                types = {
                  departmentName: filterValues[0],
                  className: entityScope,
                };
                break;
              case "subClassName":
                types = {
                  departmentName: filterValues[0],
                  className: filterValues[1],
                  subClassName: entityScope,
                };
                break;
              case "planogramName":
                types = {
                  departmentName: filterValues[0],
                  className: filterValues[1],
                  subClassName: filterValues[2],
                  planogramName: filterValues[3],
                };
                break;
              case "SKU":
                types = {
                  sku: !!filterValues[4] ? filterValues[4] : null,
                  planogramName: entityScope ?? null,
                };
                break;
              case "vendor":
                types = {
                  departmentName: filterValues[0],
                  className: filterValues[1],
                  subClassName: filterValues[2],
                  vendorName: filterValues[5],
                };
                break;
              default:
                types = { [item]: entityScope };
            }
            return {
              ...acc,
              ...types,
            };
          },
          {
            departmentName: null,
            className: null,
            subClassName: null,
          }
        );
        const currentSearch = !!groupMapping[aD] ? groupMapping[aD] : aD;
        return {
          departmentName: null,
          className: null,
          subClassName: null,
          planogramName: null,
          sku: null,
          storeId: store.id,
          viewByPlanogram: currentSearch,
          vendorName: null,
          selected: Number(displaySelectedOnly),
          itemLevel: isItemDisplay
            ? OVERRIDE_TYPE.itemLevel
            : OVERRIDE_TYPE.headerLevel,
          ...updatedFilters,
        };
      };
      dispatch(setGlobalLoader(true));
      requestMaker({
        method: "post",
        url:
          currentEntity === ENTITY_TYPES.planogram
            ? configData.AllPlanogramItemsByFilter
            : configData.GetAllDiscount,
        data: getScopeMapping(),
      })
        .then((res) => {
          const details = typeof res?.data !== "object" ? {} : res?.data;
          if (Object.keys(details).length === 0) {
            return dispatch(
              showNotifier({
                message: "No details found for the given data.",
                severity: "info",
              })
            );
          }
          setItemData({
            [entityScope]: [...details[entityScope]],
          });
          // Resetting 'D' common filter data.
          setTableData({});
          return setDetailsOnSuccess();
        })
        .catch((err) => {
          revertSelectionOnError(entityScope);
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        })
        .finally(() => {
          dispatch(setGlobalLoader(false));
        });
    },
    [dispatch, store.id, aD, filterValues, currentEntity]
  );

  const isDataPresent =
    Object.keys(tableData).length > 0 || Object.keys(itemData).length > 0;

  return (
    <Container maxWidth="xl" sx={{ paddingY: 2 }}>
      <Stack spacing={2}>
        <DiscountSearch
          disc={(data) => {
            setAD(data);
          }}
          selectToggle={(data) => {
            setDisplaySelectedOnly(data);
          }}
          getFilters={(data) => {
            setFilterData(data);
          }}
          getFilterValues={(data) => {
            setFilterValues(data);
          }}
          getData={(data) => {
            // Resetting item data if common filter ie. 'D' then it will not
            // execute child useEffect related to itemData change.
            setItemData({});
            setTableData(data);
          }}
          entityType={currentEntity}
        />
        {isDataPresent && (
          <AccordionList
            applyDisc={aD}
            filterData={filterData}
            Data={tableData}
            itemData={itemData}
            entityType={currentEntity}
            getItemDetails={handleItemDetails}
          />
        )}
      </Stack>
    </Container>
  );
};

export default DiscountAndPlanogram;
